import React, { useEffect } from "react";
import { fetchAllCountries } from '../../redux/features/other/otherSlice'
import { addUserCompany } from '../../redux/features/user/userSlice'
import BannerCustom from '../Banners/BannerCustom'
import Modal from "../Modal";
import {
  Box,
  Button,
  ButtonGroup,
  Select,
  Text,
  TextField,
  Checkbox
} from "@shopify/polaris";
import { t } from "i18next";
import _ from "lodash";
import { useSelector } from "react-redux";
import { isLatin } from "utils/validation";
import fetchDefaultCountry from "utils/defaultCountry";

import suitcaseIMG from "img/icons/entities/suitcase.svg";
import useAppDispatch from "hooks/useAppDispatch";
import { canAddCompanyAccount } from "helpers/ssoAccess";
import { shouldApplySandboxRestrictions } from "helpers/sandboxAccess";

const AddCompanyModal = ({ visible, onClose }) => {
  const dispatch = useAppDispatch();
  const user = useSelector((state) => state.user?.user);
  const canCreateCompany =
    canAddCompanyAccount(user) && !shouldApplySandboxRestrictions(user);

  	// Direction checkboxes
  const [selectedCategories, setSelectedCategories] = React.useState(['L`Tax', 'L`EPR', 'L`Invoice']);
  const isCategoryChecked = (category) => selectedCategories.includes(category);
  const handleCategoryChange = (category) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        		if (prev.length === 1) return prev; // cannot uncheck the last one
        return prev.filter((item) => item !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const { countriesAll } = useSelector((state) => state.other);
  const { addingUserCompany, addingUserCompanyError } = useSelector(
    (state) => state.user
  );

  const [companyData, setCompanyData] = React.useState({
    company_name: "",
    country: "",
  });

  const [errors, setErrors] = React.useState({
    company_name: null,
    country: null,
  });

  const hasErrors = Object.values(errors).some(Boolean);
  const someValueFalsy = !Object.values(companyData).every(Boolean);

  const isAddButtonDisabled = hasErrors || someValueFalsy;

  const handleCompanyDataChange = React.useCallback((field, value) => {
    setCompanyData((prevData) => ({
      ...prevData,
      [field]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: null,
    }));

    switch (field) {
      case "country":
        validateCountry(value);
        break;
      default:
        validateText(value, field);
    }
  }, []);

  const validateCountry = (country) => {
    if (country.length === 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        country: t("signUp.enterCountry"),
      }));
    }
  };

  const validateText = (value, field) => {
    if (value.length > 0 && isLatin(value)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: null,
      }));
    } else if (value.length > 0 && !isLatin(value)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "Use the Latin alphabet!",
      }));
    }
  };

  const onAddCompanyClick = () => {
    if (!canCreateCompany) {
      return;
    }
    		// Mapping of labels and numbers
    const categoryMap = {
      'L`Tax': 1,
      'L`EPR': 2,
      'L`Invoice': 3,
      'L`Tax Agent': 4,
      'L`OMP': 5,
      'tax': 1,
      'epr': 2,
      'invoice': 3,
      'tax_agent': 4,
      'omp': 5,
    };
    const app = selectedCategories.map(label => categoryMap[label]).filter(Boolean);
    dispatch(addUserCompany({ ...companyData, app })).then(onClose);
  };


  useEffect(() => {
    const fetchData = async () => {
      const defaultCountry = await fetchDefaultCountry();
      handleCompanyDataChange("country", defaultCountry);

      dispatch(fetchAllCountries());
    };

    if (visible && canCreateCompany) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, canCreateCompany, handleCompanyDataChange]);

  return (
    <Modal
      title={"Add Company Account"}
      visible={visible}
      onClose={onClose}
      description={
        <div style={{ maxWidth: "500px" }}>
          {canCreateCompany
            ? "To add a new company, please fill in the following fields"
            : "You don't have permission to create a company."}
        </div>
      }
      content={
        canCreateCompany ? (
        <>
          <br />
          <Box spacing='2' fullWidth>
            <TextField
              id='companyName'
              type='text'
              prefix={
                <img
                  src={suitcaseIMG}
                  alt='company name'
                  style={{ marginTop: 6 }}
                />
              }
              label={
                <Text color='subdued' variant='bodySm'>
                  {t("signUp.companyName")}
                </Text>
              }
              placeholder={t("signUp.companyName")}
              value={companyData.company_name}
              onChange={(value) =>
                handleCompanyDataChange("company_name", value)
              }
              error={errors.company_name}
            />
            <Select
              label={
                <Text color='subdued' variant='bodySm'>
                  {t("signUp.country")}
                </Text>
              }
              placeholder={t("signUp.country")}
              options={[
                {
                  key: "0",
                  label: "",
                  value: "",
                },
                ..._.map(countriesAll, (country, ind) => ({
                  key: `${ind}c`,
                  label: country.name_no_article,
                  value: country.code,
                })),
              ]}
              onChange={(value) => handleCompanyDataChange("country", value)}
              value={companyData.country}
              error={errors.country}
            />
          </Box>
          {addingUserCompanyError && (
            <>
              <br />
              <BannerCustom
                status={'critical'}
                message={addingUserCompanyError}
              />
            </>
          )}
        </>
        ) : (
          <>
            <br />
            <BannerCustom
              status="critical"
              message="You don't have permission to create a company."
            />
          </>
        )
      }
      footer={
        canCreateCompany ? (
        <>
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
            {['L`Tax', 'L`EPR', 'L`Invoice'].map((item) => (
              <div key={item} style={{ marginRight: 20 }}>
                <Checkbox
                  id={item}
                  value={item}
                  label={<div style={{fontWeight: 600}}>{item}</div>}
                  checked={isCategoryChecked(item)}
                  onChange={() => handleCategoryChange(item)}
                />
              </div>
            ))}
          </div>
          <ButtonGroup fullWidth>
            <Button size='large' onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant={'primary'}
              size='large'
              onClick={onAddCompanyClick}
              loading={addingUserCompany}
              disabled={isAddButtonDisabled}
            >
              Add Company
            </Button>
          </ButtonGroup>
        </>
        ) : (
          <ButtonGroup fullWidth>
            <Button size="large" onClick={onClose}>
              Close
            </Button>
          </ButtonGroup>
        )
      }
    />
  );
};

export default AddCompanyModal;
